import { TOKEN_CONFIG, API_ENDPOINTS } from "@/constants/api";
import { jwtDecode } from "jwt-decode";
import api from "@/utils/api";
import logger from "./logger";

interface JwtPayload {
    exp: number;
    iat: number;
    userAltKey: string;
    role: string;
    nickname: string;
    loginAt: string;
}

// 토큰 만료 시간을 캐싱
let tokenExpirationTime: number | null = null;
let cachedToken: string | null = null;

// 리프레시 시도 횟수 제한
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 2;

/**
 * Access Token을 로컬 스토리지에서 가져옵니다.
 */
export const getAccessToken = (): string | null => {
    return localStorage.getItem(TOKEN_CONFIG.ACCESS_TOKEN_KEY);
};

/**
 * Access Token을 로컬 스토리지에 저장합니다.
 */
export const setAccessToken = (token: string): void => {
    try {
        const decoded = jwtDecode<JwtPayload>(token);
        tokenExpirationTime = decoded.exp;
        cachedToken = token;
        localStorage.setItem(TOKEN_CONFIG.ACCESS_TOKEN_KEY, token);
    } catch (error) {
        logger.error("Invalid token format");
        clearTokens();
    }
};

/**
 * 토큰을 삭제합니다.
 */
export const clearTokens = (): void => {
    localStorage.removeItem(TOKEN_CONFIG.ACCESS_TOKEN_KEY);
    tokenExpirationTime = null;
    cachedToken = null;
};

/**
 * Access Token의 만료 여부를 확인하고 만료된 경우 갱신을 시도합니다.
 * @returns 토큰이 유효하면 false, 만료되고 갱신도 실패하면 true
 */
export const isTokenExpired = async (): Promise<boolean> => {
    const token = getAccessToken();

    // 토큰이 없는 경우
    if (!token) {
        logger.log("[Token Check] No token found");
        clearTokens(); // 안전하게 토큰 정보 초기화
        return true;
    }

    // 토큰이 변경되었거나 만료 시간이 캐시되지 않은 경우 재계산
    if (token !== cachedToken || !tokenExpirationTime) {
        try {
            const decoded = jwtDecode<JwtPayload>(token);
            tokenExpirationTime = decoded.exp;
            cachedToken = token;
            logger.log("[Token Check] Token decoded, exp:", new Date(tokenExpirationTime * 1000).toISOString());
        } catch {
            logger.log("[Token Check] Token decode failed");
            clearTokens(); // 디코딩 실패 시 토큰 삭제
            return true;
        }
    }

    const currentTime = Date.now() / 1000;
    logger.log("[Token Check] Current time:", new Date(currentTime * 1000).toISOString());
    logger.log("[Token Check] Token expires:", new Date(tokenExpirationTime * 1000).toISOString());

    // 실제 만료 여부 확인
    if (tokenExpirationTime <= currentTime) {
        logger.log("[Token Check] Token is expired");
        clearTokens(); // 만료된 토큰 즉시 삭제
        return true;
    }

    // 만료 예정 시간 확인 (버퍼 타임 고려)
    const willExpireSoon = tokenExpirationTime <= currentTime + TOKEN_CONFIG.BUFFER_TIME / 1000;
    logger.log("[Token Check] Will expire soon:", willExpireSoon);

    // 곧 만료 예정인 경우에만 갱신 시도
    if (willExpireSoon && refreshAttempts < MAX_REFRESH_ATTEMPTS) {
        refreshAttempts++;
        try {
            logger.log("[Token Check] Attempting to refresh token");
            const response = await api.post(API_ENDPOINTS.REFRESH);
            const newAccessToken = response.data.data.accessToken;
            setAccessToken(newAccessToken);
            logger.log("[Token Check] Token refreshed successfully");
            refreshAttempts = 0; // 성공 시 카운터 리셋
            return false;
        } catch (error) {
            logger.error("[Token Check] Token refresh failed:", error);
            clearTokens(); // 갱신 실패 시 즉시 토큰 삭제
            try {
                logger.log("[Token Check] Attempting to logout");
                await api.post(API_ENDPOINTS.LOGOUT);
            } catch (logoutError) {
                logger.error("[Token Check] Logout failed:", logoutError);
            }
            return true;
        }
    }

    return false;
};

/**
 * Access Token을 디코딩하여 사용자 정보를 추출합니다.
 * @returns 디코딩된 사용자 정보 또는 null
 */
export const decodeAccessToken = (): JwtPayload | null => {
    try {
        const token = getAccessToken();
        if (!token) return null;

        const decoded = jwtDecode<JwtPayload>(token);
        const currentTime = Date.now() / 1000;

        // 토큰이 만료되었으면 null 반환
        if (decoded.exp <= currentTime) {
            return null;
        }

        return decoded;
    } catch {
        return null;
    }
};

/**
 * 현재 사용자의 역할을 반환합니다.
 */
export const getUserRole = (): string | null => {
    const decoded = decodeAccessToken();
    return decoded?.role || null;
};

/**
 * 현재 사용자의 닉네임을 반환합니다.
 */
export const getUserNickname = (): string | null => {
    const decoded = decodeAccessToken();
    return decoded?.nickname || null;
};

/**
 * 현재 사용자의 고유 키를 반환합니다.
 */
export const getUserAltKey = (): string | null => {
    const decoded = decodeAccessToken();
    return decoded?.userAltKey || null;
};
